import { ELC_ACCOUNT, ELC_CHECKOUT } from '@estee/elc-service-view-names';
import { Service, ServiceLazyLoader, ViewsManager } from '@estee/elc-service';
import { emit, query, serviceQueries, events, ServiceBusLogHook } from '@estee/elc-service-bus';
import { AllUpdatedServicesTypes, root, ServiceInitializer } from '@estee/elc-universal-utils';
import { CastedDrupalProps, DrupalProps, mapConfigToProps } from './DrupalConfigStore';
import { defaultAppRoutes } from '../routes/index';
import { getServiceBusController, diContainer, serviceNames } from './diContainer';
import { ELCLogger, TriggerType } from '@estee/elc-logging';
import { PerformanceMarker } from '@estee/elc-telemetry';
import { onScriptError } from '../utils/onScriptError';

export const SESSION_MGMT_SERVICE = 'elc-service-session-mgmt';
export const TRANSLATIONS_SERVICE = 'elc-service-translations';
export const CONFIG_MANAGER_SERVICE = 'elc-service-config-manager';
export const SAVE_FOR_LATER_SERVICE = 'elc-service-save-for-later';
export const MANDATORY_SERVICES = [TRANSLATIONS_SERVICE, CONFIG_MANAGER_SERVICE];

const { name, version } = __serviceInfo__;

export class EcommApp extends Service {
    private disabledServices: string[] = [];
    protected perfMark: PerformanceMarker;

    constructor(serviceInitializer: ServiceInitializer = getServiceBusController) {
        super(name, version, serviceInitializer);
        this.initializeServiceVersions();
    }

    public async start(appConfig: DrupalProps) {
        if (this.isStarted) {
            return void 0;
        }
        // @ts-ignore
        this.logger = new ELCLogger({
            serviceName: name,
            serviceVersion: version,
            environment: appConfig.env,
            buid: appConfig.businessUnitId
        });
        root.env = appConfig.env || '';
        root.buid = appConfig.businessUnitId || '';

        // @ts-ignore
        root.GlobalServiceBus.applyHook(ServiceBusLogHook);

        appConfig.routes = Object.assign(appConfig.routes || {}, defaultAppRoutes);
        const props: CastedDrupalProps = mapConfigToProps(appConfig);
        // @ts-ignore
        this.perfMark = new PerformanceMarker(this.logger, name);
        // @ts-ignore
        new ServiceLazyLoader(this.logger, this.perfMark, props).load();
        // @ts-ignore
        root.ViewsManager = new ViewsManager(props, name, version);

        // Use @ as a service and version separator if there is at least one script
        // in page that uses that convention
        const useAtSignSeparator = Array.from(document.querySelectorAll('script'))
            .map((s) => s.src)
            .filter((src) => src.indexOf(props.cdnPath) !== -1)
            .some((src) => src.split('@').length > 1);
        const versionSeparator = useAtSignSeparator ? '@' : '/';

        const replaceFunction = (originalSrc: string) => {
            const elcSrc = originalSrc.indexOf('elc-');
            const pgSrc = originalSrc.indexOf('pg-');
            const realSrc = elcSrc > -1 ? elcSrc : pgSrc;
            const sourceInfo = originalSrc.slice(realSrc);
            const [serviceName, chunkName] = sourceInfo.split('/');
            const serviceVersion = props.services[serviceName];

            return `${props.cdnPath}/${serviceName}${versionSeparator}${serviceVersion}/${chunkName}`;
        };
        root.replaceSrc = root.replaceSrc || replaceFunction;
        // @ts-ignore
        root.onerror = onScriptError(appConfig.siteUrl, this.logger);

        await super.start(props);
        // start methods seems to have a different instance
        root.ServiceRegistry[this.name].isStarted = true;

        this.setupHttpTransport(props.defaultDomain, props.clientId);

        this.registerViews();

        diContainer.get(serviceNames.serviceBusController);

        await this.bootstrap(props);
    }

    private setupHttpTransport = (defaultDomain: string, clientId: string) => {
        root.HttpTransport.setBaseUrl(defaultDomain);
        root.HttpTransport.addGlobalHeaders({
            'Business-Unit': root.buid,
            ClientId: clientId || 'stardust-fe-client'
        });
    };

    private startMandatoryService = async (serviceName: string, props: CastedDrupalProps) => {
        const serviceRegistryInfo = Object.keys(root.ServiceRegistry).reduce((acc) => {
            const service = root.ServiceRegistry[serviceName] as AllUpdatedServicesTypes;

            return {
                ...acc,
                [serviceName]: {
                    isLazy: service.isLazy,
                    isStarted: service.isStarted,
                    // TODO Clean this code after SDFEFND-3634
                    // @ts-ignore
                    isStarting: service.isStarting
                }
            };
        }, {});

        try {
            const perfMarkTag = `start_${serviceName}`;

            this.perfMark.startMark(perfMarkTag);
            await (root.ServiceRegistry[serviceName] as AllUpdatedServicesTypes).start(props);
            this.perfMark.endMark(perfMarkTag);
        } catch (err) {
            this.logger.error({
                message: `${err}`,
                payload: {
                    error: err,
                    context: {
                        serviceName,
                        serviceRegistryInfo: serviceRegistryInfo
                    }
                },
                triggerType: TriggerType.runtimeError
            });
        }
    };

    private initializeComponentRenderer = async () => {
        try {
            if (!root.ComponentRenderer) {
                throw new Error('No ComponentRenderer on the global object');
            }

            this.perfMark.startMark('initialize_component_renderer');
            await root.ComponentRenderer.init();
            this.perfMark.endMark('initialize_component_renderer');
        } catch (err) {
            this.logger.error({
                triggerType: TriggerType.render,
                message: err.message
            });
        }
    };

    private bootstrap = async (props: CastedDrupalProps) => {
        await (root.ServiceRegistry[SESSION_MGMT_SERVICE] as AllUpdatedServicesTypes).start(props);

        await Promise.all(
            MANDATORY_SERVICES.map((serviceName) => this.startMandatoryService(serviceName, props))
        );
        emit(events.ECOMM_STARTED);

        try {
            this.perfMark.startMark('fetch_feature_flags_config');
            await this.setupDisabledServices();
            this.perfMark.endMark('fetch_feature_flags_config');

            this.perfMark.startMark('start_services');
            await this.startServices(props);
            this.perfMark.endMark('start_services');

            await this.initializeComponentRenderer();
        } catch (err) {
            this.logger.error({
                message: `${err}`,
                payload: {
                    error: err
                },
                triggerType: TriggerType.render
            });
        }
    };

    private startServices = async (props: CastedDrupalProps) => {
        const servicesToInitialize = Object.keys(root.ServiceRegistry).filter(
            (serviceName) =>
                !MANDATORY_SERVICES.includes(serviceName) &&
                serviceName !== this.name &&
                serviceName !== SESSION_MGMT_SERVICE &&
                this.disabledServices.indexOf(serviceName) < 0 &&
                !(root.ServiceRegistry[serviceName] as AllUpdatedServicesTypes).isLazy &&
                !root.ServiceRegistry[serviceName].isStarted
        );
        for (const serviceName of servicesToInitialize) {
            const service = root.ServiceRegistry && root.ServiceRegistry[serviceName];
            if (service && !service.isStarted) {
                this.perfMark.startMark(`start_service_${serviceName}`);
                await service.start(props);
                emit(events.SERVICE_STARTED, { serviceName: serviceName });
                this.perfMark.endMark(`start_service_${serviceName}`);
            }
        }
    };

    public setupDisabledServices = async () => {
        const config = await query(serviceQueries.GET_CONFIG, ['isSaveForLaterFeatureEnabled']);
        if (!config.isSaveForLaterFeatureEnabled) {
            this.disabledServices.push(SAVE_FOR_LATER_SERVICE);
        }
    };

    private registerViews() {
        this.registerView(ELC_ACCOUNT, () =>
            import(/* webpackChunkName: 'Account' */ '../exported/account/Account')
        );
        this.registerView(ELC_CHECKOUT, () =>
            import(/* webpackChunkName: 'Checkout' */ '../exported/checkout/Checkout')
        );
    }

    private initializeServiceVersions() {
        // the logic related to ServiceRegistry will go away from this function once we transition all the code to root.serviceInfo
        root.getServiceVersions = () => {
            const serviceInfo = root.serviceInfo;
            const nonServiceVersions = Object.keys(serviceInfo).reduce((acc, serviceName) => {
                return { ...acc, [serviceName]: serviceInfo[serviceName].version };
            }, {});
            /* eslint-disable no-param-reassign */
            const serviceRegistryVersions = Object.keys(root.ServiceRegistry).reduce(
                (acc, serviceName) => {
                    const serviceVesion = root.ServiceRegistry[serviceName].version;
                    if (serviceVesion) {
                        return { ...acc, [serviceName]: serviceVesion };
                    }

                    return acc;
                },
                {}
            );
            /* eslint-enable no-param-reassign */
            const versions: { [key: string]: string } = {
                ...serviceRegistryVersions,
                ...nonServiceVersions
            };

            const fullInfoVersions = Object.keys(versions).reduce((acc, serviceName) => {
                const currentServiceInfo = serviceInfo[serviceName] || {};
                const branchName = currentServiceInfo.branchName || '';
                const commitHash = currentServiceInfo.commitHash || '';

                return {
                    ...acc,
                    [serviceName]: { version: versions[serviceName], branchName, commitHash }
                };
            }, {});

            console.table(fullInfoVersions);
        };
    }
}
